<template>
    <HeaderContent title="Vehicle" />
    <ActionContent
        ctaText="Vehicle"
        :filterDate="false"
        :filterModal="false"
        @action="modal = !modal"
        @search-query="searchData"
    />
    <AdminStatistics title="Vehicle Statistics" :data="statistics" />
    <VehicleForm
        :modal="modal"
        :selectedData="selectedData"
        @update:modal="updateModal"
        @addOrUpdate="getAll"
    ></VehicleForm>
    <DataTable
        :data="vehicleList"
        :header="headers"
        :custom-actions="customActions"
    >
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import VehicleForm from "./VehicleForm.vue";
import DataTable from "@/components/DataTable.vue";
import AdminStatistics from "@/components/StatisticData.vue";
import iconCar from "../../../public/icons/car.svg";
import iconBus from "../../../public/icons/bus-alt.svg";
import iconDamage from "../../../public/icons/damage.svg";
import iconOveral from "../../../public/icons/square-poll-vertical.svg";
import { ref, onMounted, inject } from "vue";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";

const auth = useAuthStore();
const $loading = useLoading();
const selectedData = ref({});
const swal = inject("$swal");
const toast = useToast();

const modal = ref(false);

const statistics = ref([]);
const updateStatistics = (summary) => {
    statistics.value = [
        {
            label: "Total",
            key: "Car",
            value: summary.car ?? 0,
            color: "info",
            icon: iconCar,
        },
        {
            label: "Total",
            key: "Bus",
            value: summary.bus ?? 0,
            color: "info",
            icon: iconBus,
        },
        {
            label: "Total",
            key: "Maintenance",
            value: (summary.maintain ?? 0),
            color: "danger",
            icon: iconDamage,
        },
        {
            label: "Total",
            key: "Overall",
            value: summary.all ?? 0,
            color: "info",
            icon: iconOveral,
        },
    ];
};

const headers = ref([
    { key: "no", label: "No", sortable: true },
    { key: "plate_number", label: "Plate Number", sortable: true },
    { key: "name", label: "Vehicle Name", sortable: true },
    { key: "type", label: "Type", sortable: true },
    { key: "seat_capacity", label: "Seat", sortable: true },
    { key: "luggage_capacity", label: "Luggage", sortable: true },
    { key: "condition", label: "Condition", sortable: true },
    { key: "purchase_year", label: "Year Bought", sortable: true },
    { key: "total_km", label: "Total KM", sortable: true },
    { key: "last_service_date", label: "Last Service", sortable: true },
    { key: "tax_payment_date", label: "Tax Payment Date", sortable: true },
    { key: "action", label: "Actions" },
]);
const customActions = ref([
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            selectedData.value = item;
            modal.value = true;
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this data?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const updateModal = (newValue) => {
    modal.value = newValue;
};

let vehicleList = ref([]);

const getAll = async (search = "") => {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle`, {
        params: {
            search: search,
        },
        headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + auth._token,
        },
    });

    const res = response.data;

    if (res.status) {
        vehicleList.value = res.data;
        updateStatistics(res.summary)
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/vehicle/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } finally {
            loader.hide();
        }
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
